import { useEffect } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import useBroadcastTranslate from "../../../hooks/useBroadcastTranslate";
import { IBroadcastFormShape } from "../../../interfaces/esim/BroadcastNotifications.types";
import {
  buildTranslationSignature,
  sourceLanguageRowIndexes
} from "../../../utils/esim/broadcastGenerationOutcome";
import { appLanguageLabel } from "../../../utils/esim/broadcastStatus";
import BroadcastTranslationRow from "./BroadcastTranslationRow";
import BroadcastTranslationsStatus from "./BroadcastTranslationsStatus";
import BroadcastTranslationsToolbar from "./BroadcastTranslationsToolbar";

interface BroadcastTranslationsProps {
  sourceLanguage: string;
}

const BroadcastTranslations: React.FC<BroadcastTranslationsProps> = ({
  sourceLanguage
}) => {
  const {
    control,
    watch,
    getValues,
    formState: { errors }
  } = useFormContext<IBroadcastFormShape>();

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "translations"
  });

  // watch(), not useWatch(): it reads _formValues at render time, so it cannot lag behind
  // `fields` and leave the right option disabled for a frame after a bulk replace.
  const usedLanguages = watch("translations").map((t) => t.applicationLanguage);

  // useWatch here: a one-render lag is harmless for a flag, and it re-renders only this subtree.
  const [defaultTitle, defaultBody] = useWatch({
    control,
    name: ["defaultTitle", "defaultBody"]
  });

  const hasSource = !!(defaultTitle || "").trim() && !!(defaultBody || "").trim();

  const {
    generating,
    lastResult,
    lastWarnings,
    lastSignature,
    generate,
    resetGeneration
  } = useBroadcastTranslate(sourceLanguage, replace);

  // Stale = the rows on screen translate a main message that is no longer what will be sent.
  // Unwarned they look identical to fresh ones, and the failure is shipping the old offer x10.
  const isStale =
    lastSignature !== null &&
    lastSignature !== buildTranslationSignature(sourceLanguage, defaultTitle, defaultBody);

  const clearAll = () => {
    replace([]);
    resetGeneration();
  };

  // The main message IS the source language, so a translation box for that same language would
  // show the admin identical text twice and contradict the dropdown above it. This can only happen
  // by switching the source to a language that was already generated; drop the row when it does,
  // and say so rather than deleting text silently.
  useEffect(() => {
    const stale = sourceLanguageRowIndexes(getValues("translations") || [], sourceLanguage);

    if (!stale.length) return;

    remove(stale);
    toast.success(
      `Përkthimi në ${appLanguageLabel(sourceLanguage)} u hoq — është tani ` +
        "gjuha e mesazhit kryesor"
    );
    // getValues and remove are identity-stable, so in practice this runs only when the source
    // changes; the early return above makes a redundant run a no-op either way.
  }, [sourceLanguage, getValues, remove]);

  return (
    <div className="border-t pt-4 mt-2">
      <BroadcastTranslationsToolbar
        generating={generating}
        isStale={isStale}
        hasSource={hasSource}
        rowCount={fields.length}
        onGenerate={generate}
        onAdd={() => append({ applicationLanguage: "", title: "", body: "" })}
        onClear={clearAll}
      />
      <p className="text-xs text-gray-500 mb-3">
        Përdoruesit e marrin njoftimin në gjuhën e aplikacionit të tyre nëse
        ekziston përkthimi.
      </p>
      <BroadcastTranslationsStatus
        isStale={isStale}
        lastResult={lastResult}
        lastWarnings={lastWarnings}
        arrayError={
          typeof errors.translations?.message === "string"
            ? errors.translations.message
            : undefined
        }
      />
      {fields.map((field, index) => (
        <BroadcastTranslationRow
          key={field.id}
          index={index}
          usedLanguages={usedLanguages}
          sourceLanguage={sourceLanguage}
          onRemove={() => remove(index)}
        />
      ))}
    </div>
  );
};

export default BroadcastTranslations;
